import { ArrowDownIcon, ArrowUpIcon, DeleteIcon, EditIcon, RepeatIcon, ViewIcon } from '@chakra-ui/icons';
import {
    Badge,
    Box,
    Button,
    Center,
    Checkbox,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    SimpleGrid,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tr,
    useDisclosure,
    VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import {
    createBsClient,
    deleteBsClient,
    getAllBsClients,
    restoreBsClient,
    updateBsClient
} from '../../BSApi';
import AccessDenied from '../../components/AccessDenied';
import Loading from '../../components/Loading/Loading';
import PopoverId from '../../components/PopoverId';
import TitleHeader from '../../components/TitleHeader';
import { hasPermission, useTableColors } from '../../Utils';

const emptyLicense = () => ({
    licenseNumber: '',
    licenseKind: '',
    issuedBy: '',
    expirationDate: '',
    notes: ''
});

const emptyForm = () => ({
    clientName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phoneNumber: '',
    email: '',
    contactName: '',
    licenses: [emptyLicense()],
    notes: '',
    isActive: true
});

const BSSClientsPage = () => {
    const coolors = useTableColors();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();

    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [newItem, setNewItem] = useState(emptyForm());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleted, setShowDeleted] = useState(false);
    const [sortField, setSortField] = useState('clientName');
    const [sortOrder, setSortOrder] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);

    const itemsPerPage = 20;

    useEffect(() => {
        loadItems();
    }, [showDeleted]);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllBsClients(showDeleted);
            if (response.success) {
                setItems(Array.isArray(response.data) ? response.data : []);
            } else {
                setError(response.error || 'Failed to load BSS clients');
            }
        } catch (err) {
            setError('Failed to load BSS clients');
        } finally {
            setLoading(false);
        }
    };

    const filteredItems = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        let data = [...items];

        if (term) {
            data = data.filter((item) =>
                [item.clientName, item.city, item.state, item.contactName, item.email, item.phoneNumber]
                    .filter(Boolean)
                    .some((value) => String(value).toLowerCase().includes(term))
            );
        }

        data.sort((a, b) => {
            const aValue = String(a?.[sortField] || '').toLowerCase();
            const bValue = String(b?.[sortField] || '').toLowerCase();
            const result = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
            return sortOrder === 'asc' ? result : -result;
        });

        return data;
    }, [items, searchTerm, sortField, sortOrder]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
    const currentItems = filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, showDeleted]);

    const toggleSort = (field) => {
        const newOrder = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
        setSortField(field);
        setSortOrder(newOrder);
    };

    const openEdit = (item) => {
        setSelectedItem({
            ...item,
            licenses: Array.isArray(item.licenses) && item.licenses.length ? item.licenses : [emptyLicense()]
        });
        onOpen();
    };

    const openDelete = (item) => {
        setSelectedItem(item);
        onDeleteOpen();
    };

    const openView = (item) => {
        setSelectedItem(item);
        onViewOpen();
    };

    const handleFieldChange = (setter, field, value) => {
        setter((prev) => ({ ...prev, [field]: value }));
    };

    const handleLicenseChange = (setter, index, field, value) => {
        setter((prev) => ({
            ...prev,
            licenses: (prev.licenses || []).map((license, i) =>
                i === index ? { ...license, [field]: value } : license
            )
        }));
    };

    const addLicense = (setter) => {
        setter((prev) => ({
            ...prev,
            licenses: [...(prev.licenses || []), emptyLicense()]
        }));
    };

    const removeLicense = (setter, index) => {
        setter((prev) => {
            const nextLicenses = (prev.licenses || []).filter((_, i) => i !== index);
            return {
                ...prev,
                licenses: nextLicenses.length ? nextLicenses : [emptyLicense()]
            };
        });
    };

    const handleCreate = async () => {
        const response = await createBsClient(newItem);
        if (response.success) {
            setNewItem(emptyForm());
            onCreateClose();
            loadItems();
        } else {
            alert(response.error || 'Failed to create client');
        }
    };

    const handleSave = async () => {
        const response = await updateBsClient(selectedItem._id, selectedItem);
        if (response.success) {
            onClose();
            loadItems();
        } else {
            alert(response.error || 'Failed to update client');
        }
    };

    const handleDelete = async () => {
        const response = await deleteBsClient(selectedItem._id);
        if (response.success) {
            onDeleteClose();
            loadItems();
        } else {
            alert(response.error || 'Failed to delete client');
        }
    };

    const handleRestore = async (id) => {
        const response = await restoreBsClient(id);
        if (response.success) {
            loadItems();
        } else {
            alert(response.error || 'Failed to restore client');
        }
    };

    const renderLicenseFields = (formState, setter) => (
        <VStack spacing={4} align="stretch" w="full">
            {(formState.licenses || []).map((license, index) => (
                <Box key={index} p={4} borderWidth="1px" borderRadius="md" w="full">
                    <HStack justify="space-between" mb={3}>
                        <Text fontWeight="bold">License #{index + 1}</Text>
                        <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeLicense(setter, index)}>
                            Remove
                        </Button>
                    </HStack>
                    <VStack spacing={3} align="stretch" w="full">
                        <FormControl w="full">
                            <FormLabel>License Number</FormLabel>
                            <Input w="full" value={license.licenseNumber || ''} onChange={(e) => handleLicenseChange(setter, index, 'licenseNumber', e.target.value)} />
                        </FormControl>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} w="full">
                            <FormControl>
                                <FormLabel>License Kind</FormLabel>
                                <Input value={license.licenseKind || ''} onChange={(e) => handleLicenseChange(setter, index, 'licenseKind', e.target.value)} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Issued By</FormLabel>
                                <Input value={license.issuedBy || ''} onChange={(e) => handleLicenseChange(setter, index, 'issuedBy', e.target.value)} />
                            </FormControl>
                        </SimpleGrid>
                        <FormControl w="full">
                            <FormLabel>Expiration Date</FormLabel>
                            <Input type="date" value={license.expirationDate ? String(license.expirationDate).slice(0, 10) : ''} onChange={(e) => handleLicenseChange(setter, index, 'expirationDate', e.target.value)} />
                        </FormControl>
                        <FormControl w="full">
                            <FormLabel>Notes</FormLabel>
                            <Textarea value={license.notes || ''} onChange={(e) => handleLicenseChange(setter, index, 'notes', e.target.value)} />
                        </FormControl>
                    </VStack>
                </Box>
            ))}
            <Button size="sm" alignSelf="flex-start" onClick={() => addLicense(setter)}>Add License</Button>
        </VStack>
    );

    if (loading) {
        return <Center mt="20%"><Loading /></Center>;
    }

    return (
        <Box maxW="full" mx="auto">
            <VStack spacing={6} align="stretch">
                {!error ? (
                    <>
                        <TitleHeader
                            title="BSS Clients"
                            buttonText={hasPermission('createBSSClient') ? 'Create New Client' : undefined}
                            onClick={hasPermission('createBSSClient') ? onCreateOpen : undefined}
                        />

                        <HStack spacing={4} mb="4" align="end">
                            <FormControl>
                                <FormLabel>Search</FormLabel>
                                <Input
                                    placeholder="Search by client, city, contact, email"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </FormControl>
                            <Checkbox isChecked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)}>
                                Show deleted
                            </Checkbox>
                        </HStack>

                        {filteredItems.length ? (
                            <>
                                <Table variant="simple" size="sm">
                                    <Thead bg={coolors.theadBg}>
                                        <Tr>
                                            <Th>#</Th>
                                            <Th cursor="pointer" onClick={() => toggleSort('clientName')}>
                                                Client Name {sortField === 'clientName' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}
                                            </Th>
                                            <Th cursor="pointer" onClick={() => toggleSort('city')}>
                                                City {sortField === 'city' && (sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />)}
                                            </Th>
                                            <Th>Contact</Th>
                                            <Th>Status</Th>
                                            <Th textAlign="right">Actions</Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {currentItems.map((item, index) => (
                                            <Tr
                                                key={item._id}
                                                borderBottom={`2px solid ${coolors.borderColor}`}
                                                bg={index % 2 === 0 ? coolors.rowBgEven : coolors.rowBgOdd}
                                                color={coolors.textColor}
                                                _hover={{ bg: coolors.rowHoverBg, color: 'white' }}
                                            >
                                                <Td><PopoverId place="BSS Clients" index={(currentPage - 1) * itemsPerPage + index + 1} content={item._id} /></Td>
                                                <Td>{item.clientName}</Td>
                                                <Td>{item.city || '-'}</Td>
                                                <Td>{item.contactName || '-'}</Td>
                                                <Td>
                                                    <Badge colorScheme={item.deletedAt ? 'red' : item.isActive ? 'green' : 'yellow'}>
                                                        {item.deletedAt ? 'Deleted' : item.isActive ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </Td>
                                                <Td textAlign="right">
                                                    <HStack justifyContent="flex-end" spacing={2}>
                                                        {hasPermission('viewBSSClients') && (
                                                            <Button size="xs" colorScheme="green" onClick={() => openView(item)}><ViewIcon /></Button>
                                                        )}
                                                        {!item.deletedAt && hasPermission('updateBSSClient') && (
                                                            <Button size="xs" colorScheme="blue" onClick={() => openEdit(item)}><EditIcon /></Button>
                                                        )}
                                                        {!item.deletedAt && hasPermission('deleteBSSClient') && (
                                                            <Button size="xs" colorScheme="red" onClick={() => openDelete(item)}><DeleteIcon /></Button>
                                                        )}
                                                        {item.deletedAt && hasPermission('restoreBSSClient') && (
                                                            <Button size="xs" colorScheme="green" onClick={() => handleRestore(item._id)}><RepeatIcon /></Button>
                                                        )}
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>

                                <HStack spacing={2} mt={4} justify="center" flexWrap="wrap">
                                    {Array.from({ length: totalPages }).map((_, index) => (
                                        <Button key={index} size="sm" onClick={() => setCurrentPage(index + 1)} colorScheme={currentPage === index + 1 ? 'teal' : 'gray'}>
                                            {index + 1}
                                        </Button>
                                    ))}
                                </HStack>
                            </>
                        ) : (
                            <AccessDenied msg="No BSS clients found!" icon="info" />
                        )}
                    </>
                ) : (
                    <AccessDenied msg={error} />
                )}
            </VStack>

            <Modal scrollBehavior="inside" size="4xl" isOpen={isViewOpen} onClose={onViewClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Client Details</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {selectedItem && (
                            <VStack spacing={6} align="stretch">
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                                    <Box><Text fontSize="sm" color="gray.500">Client Name</Text><Text fontWeight="semibold">{selectedItem.clientName || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Contact Name</Text><Text fontWeight="semibold">{selectedItem.contactName || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Phone Number</Text><Text>{selectedItem.phoneNumber || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Email</Text><Text>{selectedItem.email || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">City</Text><Text>{selectedItem.city || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">State</Text><Text>{selectedItem.state || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Zip</Text><Text>{selectedItem.zip || '-'}</Text></Box>
                                    <Box><Text fontSize="sm" color="gray.500">Status</Text><Badge colorScheme={selectedItem.deletedAt ? 'red' : selectedItem.isActive ? 'green' : 'yellow'}>{selectedItem.deletedAt ? 'Deleted' : selectedItem.isActive ? 'Active' : 'Inactive'}</Badge></Box>
                                </SimpleGrid>

                                <Box>
                                    <Text fontSize="sm" color="gray.500" mb={1}>Address</Text>
                                    <Text>{selectedItem.address1 || '-'}</Text>
                                    <Text>{selectedItem.address2 || '-'}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="gray.500" mb={2}>Notes</Text>
                                    <Text whiteSpace="pre-wrap">{selectedItem.notes || '-'}</Text>
                                </Box>

                                <Box>
                                    <Text fontSize="sm" color="gray.500" mb={2}>Licenses</Text>
                                    <VStack spacing={3} align="stretch">
                                        {(selectedItem.licenses || []).length ? selectedItem.licenses.map((license, index) => (
                                            <Box key={index} p={4} borderWidth="1px" borderRadius="md">
                                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                                    <Box><Text fontSize="sm" color="gray.500">License Number</Text><Text>{license?.licenseNumber || '-'}</Text></Box>
                                                    <Box><Text fontSize="sm" color="gray.500">License Kind</Text><Text>{license?.licenseKind || '-'}</Text></Box>
                                                    <Box><Text fontSize="sm" color="gray.500">Issued By</Text><Text>{license?.issuedBy || '-'}</Text></Box>
                                                    <Box><Text fontSize="sm" color="gray.500">Expiration Date</Text><Text>{license?.expirationDate ? String(license.expirationDate).slice(0, 10) : '-'}</Text></Box>
                                                </SimpleGrid>
                                                <Box mt={3}><Text fontSize="sm" color="gray.500">Notes</Text><Text whiteSpace="pre-wrap">{license?.notes || '-'}</Text></Box>
                                            </Box>
                                        )) : <Text>-</Text>}
                                    </VStack>
                                </Box>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onViewClose}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Confirm Deletion</ModalHeader>
                    <ModalBody>
                        Are you sure you want to delete <strong>{selectedItem?.clientName}</strong>?
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="red" mr={3} onClick={handleDelete}>Delete</Button>
                        <Button onClick={onDeleteClose}>Cancel</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" size="4xl" isOpen={isOpen} onClose={onClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Edit BSS Client</ModalHeader>
                    <ModalBody>
                        {selectedItem && (
                            <VStack spacing={4} align="stretch" w="full">
                                <FormControl><FormLabel>Client Name</FormLabel><Input value={selectedItem.clientName || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'clientName', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Address 1</FormLabel><Input value={selectedItem.address1 || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'address1', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Address 2</FormLabel><Input value={selectedItem.address2 || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'address2', e.target.value)} /></FormControl>
                                <HStack w="full" spacing={4} align="start">
                                    <FormControl><FormLabel>City</FormLabel><Input value={selectedItem.city || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'city', e.target.value)} /></FormControl>
                                    <FormControl><FormLabel>State</FormLabel><Input value={selectedItem.state || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'state', e.target.value)} /></FormControl>
                                    <FormControl><FormLabel>Zip</FormLabel><Input value={selectedItem.zip || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'zip', e.target.value)} /></FormControl>
                                </HStack>
                                <HStack w="full" spacing={4} align="start">
                                    <FormControl><FormLabel>Phone Number</FormLabel><Input value={selectedItem.phoneNumber || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'phoneNumber', e.target.value)} /></FormControl>
                                    <FormControl><FormLabel>Email</FormLabel><Input value={selectedItem.email || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'email', e.target.value)} /></FormControl>
                                </HStack>
                                <FormControl><FormLabel>Contact Name</FormLabel><Input value={selectedItem.contactName || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'contactName', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Notes</FormLabel><Textarea value={selectedItem.notes || ''} onChange={(e) => handleFieldChange(setSelectedItem, 'notes', e.target.value)} /></FormControl>
                                <Checkbox isChecked={!!selectedItem.isActive} onChange={(e) => handleFieldChange(setSelectedItem, 'isActive', e.target.checked)}>Active</Checkbox>
                                {renderLicenseFields(selectedItem, setSelectedItem)}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSave}>Save</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal scrollBehavior="inside" size="4xl" isOpen={isCreateOpen} onClose={onCreateClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Create New BSS Client</ModalHeader>
                    <ModalBody>
                        <VStack spacing={4} align="stretch" w="full">
                            <FormControl><FormLabel>Client Name</FormLabel><Input value={newItem.clientName} onChange={(e) => handleFieldChange(setNewItem, 'clientName', e.target.value)} /></FormControl>
                            <FormControl><FormLabel>Address 1</FormLabel><Input value={newItem.address1} onChange={(e) => handleFieldChange(setNewItem, 'address1', e.target.value)} /></FormControl>
                            <FormControl><FormLabel>Address 2</FormLabel><Input value={newItem.address2} onChange={(e) => handleFieldChange(setNewItem, 'address2', e.target.value)} /></FormControl>
                            <HStack w="full" spacing={4} align="start">
                                <FormControl><FormLabel>City</FormLabel><Input value={newItem.city} onChange={(e) => handleFieldChange(setNewItem, 'city', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>State</FormLabel><Input value={newItem.state} onChange={(e) => handleFieldChange(setNewItem, 'state', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Zip</FormLabel><Input value={newItem.zip} onChange={(e) => handleFieldChange(setNewItem, 'zip', e.target.value)} /></FormControl>
                            </HStack>
                            <HStack w="full" spacing={4} align="start">
                                <FormControl><FormLabel>Phone Number</FormLabel><Input value={newItem.phoneNumber} onChange={(e) => handleFieldChange(setNewItem, 'phoneNumber', e.target.value)} /></FormControl>
                                <FormControl><FormLabel>Email</FormLabel><Input value={newItem.email} onChange={(e) => handleFieldChange(setNewItem, 'email', e.target.value)} /></FormControl>
                            </HStack>
                            <FormControl><FormLabel>Contact Name</FormLabel><Input value={newItem.contactName} onChange={(e) => handleFieldChange(setNewItem, 'contactName', e.target.value)} /></FormControl>
                            <FormControl><FormLabel>Notes</FormLabel><Textarea value={newItem.notes} onChange={(e) => handleFieldChange(setNewItem, 'notes', e.target.value)} /></FormControl>
                            <Checkbox isChecked={!!newItem.isActive} onChange={(e) => handleFieldChange(setNewItem, 'isActive', e.target.checked)}>Active</Checkbox>
                            {renderLicenseFields(newItem, setNewItem)}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onCreateClose} mr={3}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleCreate}>Create</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default BSSClientsPage;